import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Star, ExternalLink, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Skeleton } from "./ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import { motion } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";

// Google "G" logo component
const GoogleLogo = ({ className = "h-5 w-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

// Star rating component
const StarRating = ({ rating }: { rating: number }) => (
  <div className="flex gap-0.5" role="img" aria-label={`${rating} out of 5 stars`}>
    {[...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating ? "fill-gold text-gold" : "fill-muted text-muted"
        }`}
      />
    ))}
  </div>
);

// Review card component
const ReviewCard = ({ review, index }: { review: any; index: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    viewport={{ once: true }}
    className="h-full"
  >
    <Card className="h-full bg-card hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-border/50 relative overflow-hidden group">
      {/* Decorative quote icon */}
      <Quote className="absolute top-4 right-4 h-12 w-12 text-azure-500/10 group-hover:text-azure-500/20 transition-colors" />
      
      <CardContent className="pt-6 pb-6 flex flex-col h-full">
        {/* Header with avatar and Google badge */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <a
              href={review.google_review_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block hover:opacity-80 transition-opacity"
              aria-label={`View ${review.reviewer_name}'s Google profile`}
            >
              {review.avatar_url ? (
                <img
                  src={review.avatar_url}
                  alt={`${review.reviewer_name}'s profile`}
                  className="h-12 w-12 rounded-full object-cover ring-2 ring-azure-500/20"
                  loading="lazy"
                />
              ) : (
                <div className="h-12 w-12 rounded-full bg-azure-500/20 flex items-center justify-center ring-2 ring-azure-500/20">
                  <span className="text-azure-500 font-semibold text-lg">
                    {review.reviewer_name.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </a>
            <div>
              <h3 className="font-semibold text-card-foreground">{review.reviewer_name}</h3>
              <StarRating rating={review.rating} />
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-full">
            <GoogleLogo className="h-4 w-4" />
            <span className="hidden sm:inline">Google</span>
          </div>
        </div>

        {/* Review excerpt */}
        <p className="text-sm text-muted-foreground mb-4 flex-1 line-clamp-5 italic leading-relaxed">
          "{review.excerpt}"
        </p>

        {/* Read on Google link */}
        <a
          href={review.google_review_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-azure-500 hover:text-azure-hover transition-colors font-medium touch-target"
          aria-label={`Read ${review.reviewer_name}'s full review on Google`}
        >
          Read full review on Google
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </CardContent>
    </Card>
  </motion.div>
);

// Dot indicators for mobile carousel
const DotIndicators = ({
  count,
  activeIndex,
  onDotClick,
}: {
  count: number;
  activeIndex: number;
  onDotClick: (index: number) => void;
}) => (
  <div className="flex justify-center gap-2 mt-6" role="tablist" aria-label="Review navigation">
    {[...Array(count)].map((_, i) => (
      <button
        key={i}
        onClick={() => onDotClick(i)}
        className={`h-2.5 w-2.5 rounded-full transition-all duration-300 touch-target ${
          i === activeIndex
            ? "bg-azure-500 w-6"
            : "bg-azure-500/30 hover:bg-azure-500/50"
        }`}
        role="tab"
        aria-selected={i === activeIndex}
        aria-label={`Go to review ${i + 1}`}
      />
    ))}
  </div>
);

export const Reviews = () => {
  const isMobile = useIsMobile();
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Carousel configuration - 1 slide on mobile, 3 on desktop
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    loop: true,
    slidesToScroll: 1,
    breakpoints: {
      "(min-width: 768px)": { slidesToScroll: 1 },
    },
  });

  const { data: reviews, isLoading } = useQuery({
    queryKey: ["reviews"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select("*")
        .eq("publish", true)
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data;
    },
  });

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on("select", onSelect);
    onSelect();

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  // Calculate aggregate rating
  const aggregateRating = reviews?.length
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : "5.0";

  if (isLoading) {
    return (
      <section id="reviews" className="py-16 md:py-24 bg-accent/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Skeleton className="h-10 w-64 mx-auto mb-4" />
            <Skeleton className="h-6 w-96 mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-72 rounded-xl" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!reviews || reviews.length === 0) {
    return null;
  }

  return (
    <section id="reviews" className="py-16 md:py-24 bg-accent/30 overflow-hidden">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
            What Boat Owners Are Saying
          </h2>
          
          {/* Aggregate Rating */}
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="flex items-center gap-2">
              <GoogleLogo className="h-6 w-6" />
              <span className="text-2xl font-bold text-foreground">{aggregateRating}</span>
            </div>
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-5 w-5 fill-gold text-gold" />
              ))}
            </div>
          </div>
          
          <p className="hidden md:block text-lg text-muted-foreground max-w-2xl mx-auto">
            Trusted by boat owners across the coast.
          </p>
        </motion.div>

        {/* Carousel */}
        <div className="relative max-w-7xl mx-auto">
          {/* Navigation Arrows - Desktop only */}
          {!isMobile && reviews.length > 3 && (
            <>
              <button
                onClick={scrollPrev}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-12 z-10 bg-card shadow-lg rounded-full p-3 hover:bg-accent transition-colors focus-ring-enhanced touch-target"
                aria-label="Previous reviews"
              >
                <ChevronLeft className="h-6 w-6 text-foreground" />
              </button>
              <button
                onClick={scrollNext}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-12 z-10 bg-card shadow-lg rounded-full p-3 hover:bg-accent transition-colors focus-ring-enhanced touch-target"
                aria-label="Next reviews"
              >
                <ChevronRight className="h-6 w-6 text-foreground" />
              </button>
            </>
          )}

          {/* Embla Carousel */}
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-6">
              {reviews.map((review, index) => (
                <div
                  key={review.id}
                  className={`flex-none ${
                    isMobile ? "w-full" : "w-full md:w-[calc(33.333%-16px)]"
                  }`}
                >
                  <ReviewCard review={review} index={index} />
                </div>
              ))}
            </div>
          </div>

          {/* Dot Indicators - Mobile only */}
          {isMobile && reviews.length > 1 && (
            <DotIndicators
              count={reviews.length}
              activeIndex={selectedIndex}
              onDotClick={scrollTo}
            />
          )}
        </div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-10"
        >
          <Button
            variant="outline"
            size="lg"
            asChild
            className="border-azure-500 text-azure-500 hover:bg-azure-500 hover:text-white"
          >
            <a
              href="https://www.google.com/search?sca_esv=93169a9b36e8388d&rlz=1C1VDKB_enUS1132US1132&si=AMgyJEtREmoPL4P1I5IDCfuA8gybfVI2d5Uj7QMwYCZHKDZ-E6KGav2bul2meAJNfn6hoxSFfdsWazn4L3o-5UtiIiVVPkcLoz7hYYnXeSTccBrFp3fBkm8JLyaUSW7QskVVpEMJGxmvIO45lqsc35jta62pGBoSsw%3D%3D&q=ShoreSight+Marine+Windows+Reviews&sa=X&ved=2ahUKEwiC1ZTVqdSRAxUSwOYEHUrVBxMQ0bkNegQIRRAE"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2"
            >
              <GoogleLogo className="h-5 w-5" />
              See All Reviews on Google
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
          <Button
            size="lg"
            asChild
            className="bg-azure-500 hover:bg-azure-hover text-white"
          >
            <a
              href="https://g.page/r/CfVgJGlAlaRYEAI/review"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2"
            >
              <Star className="h-5 w-5" />
              Leave a Google Review
            </a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
